/* eslint-disable complexity */
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { ArrowLeft, ArrowRight, DeleteForever, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Button, Divider, SxProps, Theme } from '@mui/material';
import useAxios from 'axios-hooks';
import { useFormContext } from 'react-hook-form';

import { PropertiesCard } from 'components';
import { AutocompleteFormField, CheckboxFormField, SelectFormField } from 'components/FormFields';
import strings from 'constants/index';
import { EnumValueNames, useEnum } from 'contexts/EnumValuesContext';
import { ColumnIds } from 'enums';
import { useFieldGenerator } from 'hooks/forms';
import { normalizeEnumValues } from 'utils/normalizers.utils';
import { buildRequestParams } from 'utils/pagination.utils';

import { ExtendedPermissionItem, PermissionsModel } from '../Permissions.types';
import { permissionsCardStyles } from './PermissionsCard.styles';
import PermissionsWrapper from './PermissionsWrapper';

interface PermissionsCardProps extends ExtendedPermissionItem {
  isEditing?: boolean;
  prefix: `permissions.${number}`;
  onDelete: () => void;
  sx?: SxProps<Theme>;
}

const PermissionsCard = ({
  childPermissions,
  resourceValue,
  // trustLevel, @TODO
  parentResourceType,
  parentResourceValue,
  resourceType,
  isAdminPermission,
  create,
  read,
  update,
  delete: deletePermission,
  prefix,
  onDelete,
  sx,
}: PermissionsCardProps) => {
  const { setValue, clearErrors } = useFormContext();
  const resourcesTypesEnum = useEnum(EnumValueNames.Resource);
  const [expanded, setExpanded] = useState(false);
  const [childrenVisible, setChildrenVisible] = useState(false);
  const { generateField } = useFieldGenerator<PermissionsModel>();
  const resourceTypeValue = resourcesTypesEnum.find(
    item =>
      String(item.value) === String(resourceType) ||
      item.name.toLowerCase() === String(resourceType).toLowerCase(),
  );
  const filters = [{ columnId: ColumnIds.Id, value: resourceValue as any }];
  const competitionFilter = [];
  const resourcetype = useEnum(EnumValueNames.Resource);
  const resourceTypeId = resourcetype.find(resource => resource.name === 'Sport')?.value;
  let parentResourceValueId;
  if (parentResourceValue) {
    parentResourceValueId =
      typeof parentResourceValue === 'object' ? parentResourceValue.id : parentResourceValue;
  }
  if (
    resourceTypeValue?.name === 'Competition' &&
    String(parentResourceType) === String(resourceTypeId)
  ) {
    competitionFilter.push({ columnId: 'sportId', value: parentResourceValueId });
  }

  const resourceTypeValueUrl = useMemo(() => {
    if (resourceTypeValue?.name.toLowerCase() === 'playermanagementbysport') {
      return '/sports/trimmed';
    }

    return `/${resourceTypeValue?.name.toLowerCase()}s/trimmed`;
  }, [resourceTypeValue]);

  const [{ data: resourceValueResponse }] = useAxios(
    {
      url: resourceTypeValueUrl,
      params: buildRequestParams({
        filters,
      }),
    },
    {
      manual:
        resourceTypeValue?.name == null ||
        resourceValue === '' ||
        typeof resourceValue !== 'string',
    },
  );
  const resourceValueName =
    resourceValue === ''
      ? null
      : typeof resourceValue === 'string'
        ? `[${resourceValue}] - ${resourceValueResponse?.items?.[0]?.name}` ||
          `${strings.unknown_id} ${resourceValue}`
        : resourceValue?.name;

  useEffect(() => {
    if (
      resourceValueResponse?.items?.[0] &&
      typeof resourceValue === 'string' &&
      resourceValue !== ''
    ) {
      setValue(
        `${prefix}.resourceValue`,
        {
          id: parseInt(resourceValue, 10) || resourceValue,
          name: resourceValueResponse.items[0].name,
        },
        { shouldDirty: false, shouldTouch: false, shouldValidate: false },
      );
    }
  }, [resourceValueResponse, resourceValue, prefix, setValue]);

  const dynamicSearchOptions = [
    { columnId: ColumnIds.Name, type: 'string' },
    { columnId: ColumnIds.Id, type: 'number' },
  ];
  useEffect(() => {
    if (isAdminPermission) {
      setValue(`${prefix}.create`, true, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setValue(`${prefix}.read`, true, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setValue(`${prefix}.update`, true, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setValue(`${prefix}.delete`, true, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
    }
  }, [isAdminPermission, prefix, setValue]);
  const getUrlByResourceTypeValue = (resourceTypeValueString: string | undefined) => {
    if (!resourceTypeValueString) {
      return '';
    }
    if (resourceTypeValueString === 'PlayerManagementBySport') {
      return '/sports/trimmed';
    }
    return `/${resourceTypeValueString.toLowerCase()}s/trimmed`;
  };
  const getPrimaryValue = (): ReactNode => {
    if (resourceType === '' || resourceValue === '') {
      return (
        <Box fontWeight="bold" color="red">
          {strings.incomplete_entry}
        </Box>
      );
    }
    return `${resourceTypeValue?.name} ${resourceValueName}, ${childPermissions?.length} ${strings.child_permissions}`;
  };

  const baseItems = [
    {
      value: getPrimaryValue(),
      name: `${strings.permissions}: ${
        [
          isAdminPermission && strings.role_curator,
          create && strings.create,
          update && strings.update,
          read && strings.read,
          deletePermission && strings.delete,
        ]
          .filter(item => item)
          .join(', ') || strings.not_applicable
      }`,
      actions: [
        {
          onClick: () => {
            setChildrenVisible(false);
            setExpanded(!expanded);
          },
          icon: expanded ? (
            <ExpandLess data-testid="expand-less" />
          ) : (
            <ExpandMore data-testid="expand-more" />
          ),
        },
      ],
    },
  ];

  const items = [
    {
      name: strings.child_permissions_label,
      value: String(childPermissions.length),
      actions: [
        {
          onClick: () => setChildrenVisible(!childrenVisible),
          icon: childrenVisible ? (
            <ArrowLeft data-testid="arrow-left" />
          ) : (
            <ArrowRight data-testid="arrow-right" />
          ),
        },
      ],
    },
    {
      ...generateField(`${prefix}.resourceType`),
      name: strings.resource_type,
      value: resourceTypeValue?.name || '',
      editComponent: (
        <SelectFormField
          data-testid="resource-type"
          required
          label={strings.resource_type}
          name={`${prefix}.resourceType`}
          values={normalizeEnumValues(
            resourcesTypesEnum.filter(
              item =>
                item.name === strings.sport ||
                item.name === strings.competition ||
                item.name === 'PlayerManagementBySport',
            ),
          )}
          onChange={() => {
            setValue(`${prefix}.resourceValue`, '', {
              shouldValidate: true,
              shouldTouch: true,
              shouldDirty: true,
            });
          }}
        />
      ),
    },
    {
      ...generateField(`${prefix}.resourceValue`),
      name: strings.resource_value,
      value: resourceValueName || '',
      editComponent: (
        <AutocompleteFormField
          required
          label={strings.resource_value}
          name={`${prefix}.resourceValue`}
          defaultFilters={competitionFilter}
          searchTermColumnId={parentResourceType === 'sport' ? ColumnIds.Id : ColumnIds.Name}
          // dataSource={`/${resourceTypeValue?.name.toLowerCase()}s/trimmed`}
          dataSource={getUrlByResourceTypeValue(resourceTypeValue?.name)}
          disabled={!resourceTypeValue}
          dynamicSearch={dynamicSearchOptions}
          onClose={() => {
            clearErrors();
          }}
        />
      ),
    },
  ];

  return (
    <Box display="flex" alignItems="flex-start" sx={sx} data-testid="permissions-card">
      <PropertiesCard
        sx={permissionsCardStyles.card}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        properties={expanded ? [...baseItems, ...items] : baseItems}
      >
        {expanded && (
          <>
            <Divider />
            <Box display="flex" my={2} justifyContent="space-between">
              <CheckboxFormField
                data-testid="admin-checkbox"
                name={`${prefix}.isAdminPermission`}
                labelPlacement="bottom"
                label={strings.role_curator}
              />
              <CheckboxFormField
                data-testid="create-checkbox"
                disabled={isAdminPermission}
                name={`${prefix}.create`}
                labelPlacement="bottom"
                label={strings.create}
              />
              <CheckboxFormField
                disabled={isAdminPermission}
                data-testid="read-checkbox"
                name={`${prefix}.read`}
                labelPlacement="bottom"
                label={strings.read}
              />
              <CheckboxFormField
                disabled={isAdminPermission}
                data-testid="update-checkbox"
                name={`${prefix}.update`}
                labelPlacement="bottom"
                label={strings.update}
              />
              <CheckboxFormField
                disabled={isAdminPermission}
                data-testid="delete-checkbox"
                name={`${prefix}.delete`}
                labelPlacement="bottom"
                label={strings.delete}
              />
            </Box>
            <Divider />
            <Box display="flex" my={2} justifyContent="center">
              <Button
                data-testid="delete-permission-card"
                variant="contained"
                startIcon={<DeleteForever />}
                onClick={onDelete}
              >
                {strings.remove_permission}
              </Button>
            </Box>
          </>
        )}
      </PropertiesCard>

      {childrenVisible && <PermissionsWrapper prefix={`${prefix}.childPermissions`} />}
    </Box>
  );
};

export default PermissionsCard;
